/**
 * Browser notifications for Focus phase changes — opt-in, one tab only.
 *
 * The Mac menu-bar app is the primary notifier; this path exists for
 * browser-only use. With several tabs open exactly one should speak:
 * `holdAlertLeadership` elects a leader through the Web Locks API (the lock
 * passes on when the leader tab closes). Without Web Locks every tab is a
 * leader, and two fallbacks keep it to one alert — `claimAlert` (first tab to
 * write the shared localStorage key wins) and the Notification `tag` (a
 * second notification with the same tag replaces the first).
 */
const ALERT_PREFIX = "dux-focus:alerted:";
const ALERT_TTL_MS = 2 * 24 * 60 * 60 * 1000;
const LOCK_NAME = "dux-focus:alerts";
const TAG_PREFIX = "dux-focus:";

export function notificationsSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export function notificationPermission(): NotificationPermission | "unsupported" {
  return notificationsSupported() ? Notification.permission : "unsupported";
}

/** Ask for permission — only ever from inside the click that turns notify on. */
export async function requestNotifyPermission(): Promise<boolean> {
  if (!notificationsSupported()) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  return (await Notification.requestPermission()) === "granted";
}

/** True for the first caller per key — across tabs, through shared storage. */
export function claimAlert(key: string): boolean {
  const storageKey = ALERT_PREFIX + key;
  try {
    if (window.localStorage.getItem(storageKey)) return false;
    window.localStorage.setItem(storageKey, String(Date.now()));
  } catch {
    // No storage — the Notification tag still collapses duplicates.
  }
  return true;
}

/** Drop dedupe keys older than two days (a handful accrue per focus day). */
export function sweepAlertKeys(nowMs: number): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith(ALERT_PREFIX)) continue;
      const at = Number(window.localStorage.getItem(key));
      if (!Number.isFinite(at) || nowMs - at > ALERT_TTL_MS) stale.push(key);
    }
    for (const key of stale) window.localStorage.removeItem(key);
  } catch {
    // Storage unavailable — nothing to sweep.
  }
}

export function showNotification(key: string, title: string, body: string): void {
  if (!notificationsSupported() || Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, { body, tag: TAG_PREFIX + key });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // Platforms that only notify through a service worker — stay silent.
  }
}

/**
 * Compete for the alert lock; `onChange(true)` once this tab holds it. The
 * returned function releases (or withdraws a pending request).
 */
export function holdAlertLeadership(onChange: (leader: boolean) => void): () => void {
  if (!("locks" in navigator) || !navigator.locks) {
    onChange(true);
    return () => onChange(false);
  }
  let release: () => void = () => undefined;
  const held = new Promise<void>((resolve) => {
    release = resolve;
  });
  const controller = new AbortController();
  navigator.locks
    .request(LOCK_NAME, { signal: controller.signal }, async () => {
      onChange(true);
      await held;
      onChange(false);
    })
    .catch(() => undefined); // AbortError when withdrawn before the grant
  return () => {
    controller.abort();
    release();
  };
}
